/*
  Inquiry lifecycle on the client: Draft -> Sent -> Quotation received -> Ordered.
  The server derives the status (server/services/inquiryLifecycle.js); these helpers keep the list, the detail page
  and the company tab consistent while the statuses lookup (/api/inquiries/statuses) is still loading.
*/

export const INQUIRY_STATUS_IDS = Object.freeze({
  DRAFT: 1,
  SENT: 2,
  QUOTATION_RECEIVED: 3,
  ORDERED: 4
});

export const INQUIRY_STATUSES_FALLBACK = Object.freeze([
  { InquiryStatusID: 1, Code: 'DRAFT', Status: 'Draft' },
  { InquiryStatusID: 2, Code: 'SENT', Status: 'Sent' },
  { InquiryStatusID: 3, Code: 'QUOTATION_RECEIVED', Status: 'Quotation received' },
  { InquiryStatusID: 4, Code: 'ORDERED', Status: 'Ordered' }
]);

// Statuses a user can pick by hand; Ordered follows from the linked purchase order(s)
export const MANUAL_INQUIRY_STATUS_IDS = Object.freeze([INQUIRY_STATUS_IDS.DRAFT, INQUIRY_STATUS_IDS.SENT, INQUIRY_STATUS_IDS.QUOTATION_RECEIVED]);

export function deriveInquiryStatusId(item = {}) {
  if (item.InquiryStatusID) return Number(item.InquiryStatusID);
  if (Number(item.LinkedPurchaseOrderCount) > 0) return INQUIRY_STATUS_IDS.ORDERED;
  if (item.Ontvangen) return INQUIRY_STATUS_IDS.QUOTATION_RECEIVED;
  if (item.Verstuurd) return INQUIRY_STATUS_IDS.SENT;
  return INQUIRY_STATUS_IDS.DRAFT;
}

export function getInquiryStatusLabel(statusId, statuses = []) {
  const id = Number(statusId);
  const source = Array.isArray(statuses) && statuses.length > 0 ? statuses : INQUIRY_STATUSES_FALLBACK;
  return source.find((status) => Number(status.InquiryStatusID) === id)?.Status || INQUIRY_STATUSES_FALLBACK.find((status) => status.InquiryStatusID === id)?.Status || 'Draft';
}

export function getInquiryStatusTone(statusId) {
  const id = Number(statusId);
  if (id === INQUIRY_STATUS_IDS.ORDERED) return 'success';
  if (id === INQUIRY_STATUS_IDS.QUOTATION_RECEIVED) return 'attention';
  if (id === INQUIRY_STATUS_IDS.SENT) return 'info';
  return undefined;
}

// linked_purchase_orders comes from the list API as a JSON string (FOR JSON PATH) or NULL; the detail API sends an array
export function parseLinkedPurchaseOrders(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

// '2026-09-15' -> '15 Sep 2026'; an ISO datetime -> '15 Sep 2026, 10:32'
export function formatMoment(value) {
  if (!value) return '';
  const text = String(value);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(text);
  const date = new Date(dateOnly ? `${text}T00:00:00` : text);
  if (Number.isNaN(date.getTime())) return text.substring(0, 10);
  const day = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  if (dateOnly) return day;
  return `${day}, ${date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
}
